/**
 * Pre-storyboard generation pipeline.
 *
 * Takes the user's characters/elements after Cast & World and produces:
 *   1. Missing character + element portraits (parallel)
 *   2. Missing costumes for characters (parallel)
 *   3. Optional virtual try-on (parallel, gated by user confirmation)
 *
 * Pure JS — no React state. Caller passes callbacks to receive progress
 * updates, persist media, deduct credits, and decide whether to run try-on.
 *
 * Extracted from CharacterSelection.jsx so the same pipeline can be invoked
 * from the Tier Selection page after the user picks a tier.
 */

import { generateImage, generateElementImage, generateCharacterPortrait } from './aiService';
import { generateCharacterPortraitWithGemini } from './geminiService';
import { fetchImageViaProxy, uploadDataUriToBackend, isAlreadyPersisted } from './backendService';
import { CREDIT_COSTS } from '../types';
import { buildCharacterSheetPrompt } from '../utils/characterSheetPrompt';
import { buildCostumePrompt } from '../utils/costumePrompt';

const persistToGCS = async (imageUrl) => {
    if (!imageUrl) return imageUrl;
    if (isAlreadyPersisted(imageUrl)) return imageUrl;
    const userEmail = sessionStorage.getItem('userEmail');
    if (imageUrl.startsWith('data:')) {
        try {
            const gcsUrl = await uploadDataUriToBackend(imageUrl, userEmail);
            if (gcsUrl) return gcsUrl;
        } catch { /* keep original */ }
        return imageUrl;
    }
    if (!imageUrl.startsWith('http')) return imageUrl;
    try {
        const base64 = await fetchImageViaProxy(imageUrl);
        if (!base64 || !base64.startsWith('data:')) return imageUrl;
        const gcsUrl = await uploadDataUriToBackend(base64, userEmail);
        return gcsUrl || base64;
    } catch {
        return imageUrl;
    }
};

const runPool = async (items, concurrency, taskFn) => {
    const queue = [...items];
    const worker = async () => {
        while (queue.length > 0) {
            const item = queue.shift();
            if (!item) break;
            await taskFn(item);
        }
    };
    await Promise.all(Array.from({ length: Math.min(concurrency, queue.length) }, () => worker()));
};

/**
 * Run the full pre-storyboard generation pipeline.
 *
 * @param {Object} args
 * @param {Object} args.project - The project (need aspectRatio + script).
 * @param {Array}  args.characters - Initial character/element/narrator list.
 * @param {(needsTryOn: Array) => Promise<string[]>} [args.askTryOn]
 *        Callback returning the array of character ids the user wants try-on
 *        for. Return [] to skip. Omit to skip try-on entirely.
 * @param {(state: {current,total,name,step}) => void} [args.onProgress]
 * @param {(urls: string[]) => void} [args.onSaveMedia]
 * @param {(credits: number) => void} [args.onTrackCreditsUsed]
 * @param {() => Promise<void>} [args.onRefreshCredits]
 *
 * @returns {Promise<Array>} Updated characters with images, costumes,
 *          try-on portraits applied.
 */
export async function runPreStoryboardPipeline({
    project,
    characters,
    askTryOn,
    onProgress,
    onSaveMedia,
    onTrackCreditsUsed,
    onRefreshCredits,
}) {
    const aspectRatio = project?.aspectRatio || '16:9';
    const script = project?.script || '';
    const validChars = characters.filter(c => c.name?.trim() && c.description?.trim());
    if (validChars.length === 0) return validChars;

    const needsPortrait = validChars.filter(c => !c.image && c.category !== 'narrator');
    const needsCostume = validChars.filter(c =>
        (c.category === 'character' || !c.category) &&
        c.category !== 'narrator' &&
        (!c.costumes || c.costumes.length === 0)
    );

    const charById = new Map(validChars.map(c => [c.id, { ...c }]));
    const PORTRAIT_CONCURRENCY = 5;

    let p12Done = 0;
    const p12Total = needsPortrait.length + needsCostume.length;
    const tickP12 = (charName, kind) => {
        p12Done++;
        if (onProgress) onProgress({ current: p12Done, total: p12Total, name: charName, step: kind });
    };
    if (p12Total > 0 && onProgress) {
        onProgress({ current: 0, total: p12Total, name: 'Starting…', step: 'portrait' });
    }

    const portraitTask = async (char) => {
        if (onProgress) onProgress({ current: p12Done, total: p12Total, name: char.name, step: 'portrait' });
        try {
            let imageUrl;
            if (char.category === 'element') {
                const result = await generateElementImage(char.description || char.name, [], aspectRatio, 'default');
                imageUrl = result?.imageUrl || result?.images?.[0];
                if (result?.creditsUsed && onTrackCreditsUsed) onTrackCreditsUsed(result.creditsUsed);
            } else {
                // Generate a CHARACTER SHEET (4 full-body turnaround + 4 close-ups)
                // instead of a single stand-alone portrait. Layout adapts to
                // project aspect ratio (landscape/square/portrait).
                const sheetPrompt = buildCharacterSheetPrompt(char.name, char.description, aspectRatio);
                const result = await generateCharacterPortraitWithGemini(
                    sheetPrompt, [], [], null, aspectRatio, char.category || 'character', 'default'
                );
                imageUrl = result?.image || result;
                if (onTrackCreditsUsed) onTrackCreditsUsed(result?.creditsUsed || CREDIT_COSTS.CHARACTER_PORTRAIT || 0);
            }
            if (imageUrl) {
                const gcsUrl = await persistToGCS(imageUrl);
                const existing = charById.get(char.id) || char;
                charById.set(char.id, { ...existing, image: gcsUrl });
                if (onSaveMedia) onSaveMedia([gcsUrl]);
            }
        } catch (err) { console.error(`Portrait failed for ${char.name}:`, err); }
        finally { tickP12(char.name, 'portrait'); }
    };

    const costumeTask = async (char) => {
        if (onProgress) onProgress({ current: p12Done, total: p12Total, name: char.name, step: 'costume' });
        try {
            const prompt = buildCostumePrompt(char.name, char.description, script);
            const result = await generateImage(prompt, 1, 'default', '1:1', [], true, null);
            const costumeUrl = result?.images?.[0] || result?.imageUrl;
            if (costumeUrl) {
                const gcsUrl = await persistToGCS(costumeUrl);
                const existing = charById.get(char.id) || char;
                charById.set(char.id, { ...existing, costumes: [...(existing.costumes || []), gcsUrl] });
                if (onSaveMedia) onSaveMedia([gcsUrl]);
                if (result?.creditsUsed && onTrackCreditsUsed) onTrackCreditsUsed(result.creditsUsed);
            }
        } catch (err) { console.error(`Costume failed for ${char.name}:`, err); }
        finally { tickP12(char.name, 'costume'); }
    };

    await Promise.all([
        runPool(needsPortrait, PORTRAIT_CONCURRENCY, portraitTask),
        runPool(needsCostume, PORTRAIT_CONCURRENCY, costumeTask),
    ]);

    let updatedChars = validChars.map(c => charById.get(c.id) || c);

    // ---- Phase 3: try-on (gated on user confirmation) ----
    const candidates = updatedChars.filter(c =>
        (c.category === 'character' || !c.category) &&
        c.image &&
        ((c.costumes && c.costumes.length > 0) || (c.costumeAndAccessoryImages && c.costumeAndAccessoryImages.length > 0))
    );

    if (candidates.length > 0 && askTryOn) {
        const selectedIds = await askTryOn(candidates);
        if (selectedIds && selectedIds.length > 0) {
            const filtered = candidates.filter(c => selectedIds.includes(c.id));
            const TRYON_CONCURRENCY = 5;
            let tryOnDone = 0;
            if (onProgress) onProgress({ current: 0, total: filtered.length, name: 'Starting…', step: 'try-on' });

            const tryOnTask = async (char) => {
                if (onProgress) onProgress({ current: tryOnDone, total: filtered.length, name: char.name, step: 'try-on' });
                try {
                    const costumeImgs = [...(char.costumeAndAccessoryImages || []), ...(char.costumes || [])].filter(Boolean);
                    // Re-render the FULL 8-panel character sheet with the
                    // costume applied across every panel — matches the manual
                    // "Try on" button in Cast & World. Previously this
                    // pipeline used a single medium-shot portrait prompt,
                    // which produced a different (single-image) try-on shape
                    // than what the user got from the manual flow.
                    const tryOnPrompt = buildCharacterSheetPrompt(char.name, char.description, aspectRatio, { tryOnCostume: true });
                    const result = await generateCharacterPortrait(
                        tryOnPrompt, [char.image], costumeImgs, '', aspectRatio, 'character', 'default'
                    );
                    const tryOnUrl = result?.image || result?.imageUrl || result?.images?.[0];
                    if (tryOnUrl) {
                        const gcsUrl = await persistToGCS(tryOnUrl);
                        const existing = charById.get(char.id) || char;
                        charById.set(char.id, {
                            ...existing,
                            originalPortrait: existing.originalPortrait || existing.image,
                            image: gcsUrl,
                        });
                        if (onSaveMedia) onSaveMedia([gcsUrl]);
                        if (result?.creditsUsed && onTrackCreditsUsed) onTrackCreditsUsed(result.creditsUsed);
                    }
                } catch (err) { console.error(`Try-on failed for ${char.name}:`, err); }
                finally {
                    tryOnDone++;
                    if (onProgress) onProgress({ current: tryOnDone, total: filtered.length, name: char.name, step: 'try-on' });
                }
            };
            await runPool(filtered, TRYON_CONCURRENCY, tryOnTask);
            updatedChars = updatedChars.map(c => charById.get(c.id) || c);
        }
    }

    if (onRefreshCredits) {
        try { await onRefreshCredits(); } catch { /* non-fatal */ }
    }

    return updatedChars;
}

/**
 * Inspect a project + character list and return how many of each
 * generation step still need to run. Used by the Tier Selection page
 * to show an itemised cost breakdown.
 */
export function countPendingGeneration(project, characters) {
    const valid = (characters || []).filter(c => c.name?.trim() && c.description?.trim());
    const portraits = valid.filter(c => !c.image && c.category !== 'narrator').length;
    const costumes = valid.filter(c =>
        (c.category === 'character' || !c.category) &&
        c.category !== 'narrator' &&
        (!c.costumes || c.costumes.length === 0)
    ).length;
    const elements = valid.filter(c => c.category === 'element' && !c.image).length;
    const tryOnCandidates = valid.filter(c =>
        (c.category === 'character' || !c.category) &&
        ((c.costumes && c.costumes.length > 0) ||
         (c.costumeAndAccessoryImages && c.costumeAndAccessoryImages.length > 0) ||
         (!c.image)) // assume costume will exist after generation, so try-on is possible
    ).length;
    const sceneCount = (project?.scenes || []).length;
    const shotCount = (project?.scenes || []).reduce((n, s) => n + (s.shots?.length || 0), 0);
    return {
        portraits,
        costumes,
        elements,
        tryOnCandidates,
        sceneCount,
        // If storyboard hasn't been generated yet we don't know shot count;
        // assume the project's duration / 5s per shot as a rough planning value.
        plannedShots: shotCount > 0 ? shotCount : Math.max(4, Math.round(parseDurationSeconds(project?.duration) / 5)),
        storyboardPending: sceneCount === 0,
    };
}

const parseDurationSeconds = (duration) => {
    if (!duration) return 30;
    const m = String(duration).match(/(\d+)/);
    return m ? parseInt(m[1], 10) : 30;
};
